// src/pages/RequestDemo.jsx
import React, { useState } from "react";
import { motion } from "framer-motion";
import { requestDemo } from "../api/demoApi";
import FaqSection from '../components/FaqSection';
import CalendlyEmbed from '../components/CalendlyEmbed';



// Assuming AnimatedSection and animation variants are defined elsewhere
// Using placeholder definitions similar to your ContactForm for this example.
const AnimatedSection = ({ children, animationVariants, className, triggerOnce, staggerChildren, tag = "div" }) => {
  const MotionComponent = motion[tag];
  return (
    <MotionComponent
      variants={animationVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: triggerOnce !== false }} // Default triggerOnce to true
      className={className}
      transition={staggerChildren ? { staggerChildren } : undefined}
    >
      {children}
    </MotionComponent>
  );
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const fadeIn = {
  // This parent variant staggers its direct motion children
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5, staggerChildren: 0.1 } },
};

const staggerItemFadeInUp = {
  // This variant is for the children being staggered
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const RequestDemo = () => {
  const initialFormData = {
    fullName: "",
    company: "",
    email: "",
    phoneNumber: "",
    modules: {
      module_universal_adapters: false,
      module_blox_blueprint: true, // Checked by default
      module_bpsc_compiler: false,
      module_frontier_blox_engine: false,
    },
    message: "",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState({ type: "", message: "" });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData((prevState) => ({
        ...prevState,
        modules: {
          ...prevState.modules,
          [name]: checked,
        },
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset form status
    setFormStatus({ type: "", message: "" });
    setIsSubmitting(true);

    try {
      const response = await requestDemo(formData);
      console.log("[RequestDemo] Submission successful:", response);

      setFormStatus({
        type: "success",
        message: "Thank you for requesting a demo! We will contact you soon.",
      });

      setFormData(initialFormData); // Reset form
    } catch (error) {
      console.error("[RequestDemo] Submission error:", error);
      setFormStatus({
        type: "error",
        message: error.message || "Failed to submit demo request. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Checkbox definitions
  const moduleOptions = [
    { name: "module_universal_adapters", label: "Universal Adapters" },
    { name: "module_blox_blueprint", label: "BloxBlueprint" },
    { name: "module_bpsc_compiler", label: "BPSC Compiler" },
    { name: "module_frontier_blox_engine", label: "FrontierBlox Engine" },
  ];

  // Using input styling from your original ContactForm.js
  const inputClass =
    "w-full bg-gray-50 border border-gray-300 text-black rounded-lg p-4 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent";
  const labelClass = "block text-sm font-medium text-gray-700"; // Added for checkbox legend

  return (
    // Page container styling from your ContactForm.js
    <>
      <div className="bg-white flex justify-center py-16 px-4 sm:px-6 lg:py-24 lg:px-8 mt-20">
        {/* Inner content container styling from your ContactForm.js */}
        <div className="bg-white text-black max-w-6xl w-full">
          <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} className="mb-4 text-left">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">Join Us in Shaping Tomorrow's Financial Landscape.</h1>
          </AnimatedSection>

          <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} className="mb-6 text-left">
            <p className="text-gray-600 text-lg max-w-3xl">
              Are you ready to explore our innovative modules? Whether you're a developer, a potential partner, or just curious about what we offer, we
              encourage you to reach out and schedule a demo with us!
            </p>
          </AnimatedSection>

          {/* Form status message */}
          {formStatus.message && (
            <div className={`mb-6 p-4 rounded-lg ${formStatus.type === "success" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
              {formStatus.message}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <AnimatedSection triggerOnce={false} animationVariants={fadeIn} className="space-y-6">
              {/* Grid for inputs */}
              <motion.div variants={staggerItemFadeInUp} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    required
                    className={inputClass}
                    value={formData.fullName}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="company"
                    placeholder="Company"
                    className={inputClass}
                    value={formData.company}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    required
                    className={inputClass}
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    name="phoneNumber"
                    placeholder="Phone Number"
                    className={inputClass}
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                </div>
              </motion.div>

              {/* Checkbox Section - UPDATED TO FLEX FOR INLINE */}
              <motion.fieldset variants={staggerItemFadeInUp} className="pt-2">
                <legend className={`${labelClass} mb-3`}>Choose the modules that best suit your needs:</legend>
                <div className="flex flex-wrap gap-x-36 gap-y-3 items-center">
                  {" "}
                  {/* <-- Changed to flex for inline */}
                  {moduleOptions.map((module) => (
                    <div key={module.name} className="flex items-center">
                      <input
                        id={module.name}
                        name={module.name}
                        type="checkbox"
                        checked={formData.modules[module.name]}
                        onChange={handleChange}
                        className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        disabled={isSubmitting}
                      />
                      <label htmlFor={module.name} className="ml-2 block text-sm text-gray-900">
                        {module.label}
                      </label>
                    </div>
                  ))}
                </div>
              </motion.fieldset>

              {/* Message Textarea */}
              <motion.div variants={staggerItemFadeInUp}>
                <textarea
                  name="message"
                  placeholder="Message"
                  rows="5"
                  required
                  className={`${inputClass} resize-none min-h-[120px]`}
                  value={formData.message}
                  onChange={handleChange}
                  disabled={isSubmitting}
                ></textarea>
              </motion.div>

              {/* Submit Button */}
              <motion.div variants={staggerItemFadeInUp} className="pt-2">
                <button
                  type="submit"
                  className="bg-purple-600 text-white py-3 px-8 rounded-lg font-semibold hover:bg-purple-700 transition duration-300 ease-in-out disabled:opacity-70"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit Now"}
                </button>
              </motion.div>
            </AnimatedSection>
          </form>
        </div>
      </div>
      <div className="bg-gray-50 flex justify-center py-16 px-4">
        <div className="max-w-6xl w-full text-center">
          <h2 className="text-3xl font-semibold text-black">
            Or, Schedule a Meeting Directly
          </h2>

          {/*  calendly embed */}
          <CalendlyEmbed url="https://calendly.com/fode-d-ua/30min" />

        </div>
      </div>
      <FaqSection />

    </>
  );
};

export default RequestDemo;
